import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/app/shell/page-header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useCan, useCanWithoutBuilding } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { AcceptanceFormsPanel } from "./-components/forms/acceptance-forms-panel";
import { OfferLettersPanel } from "./-components/offer-letters-panel";
import { OneBuildingCard } from "@/components/app/workspace/one-building-card";

export const Route = createFileRoute(
  "/_authenticated/_administration/settings/ats-offer-templates",
)({
  beforeLoad: ({ context }) => guardRoute(context.me, "ats-offer-templates"),
  component: AtsOfferTemplatesSettings,
});

/** The two halves of an offer: the letter the candidate is sent, and the form they fill in when
 * they accept it. Both belong to one building, and both are wording rather than terms: the pay
 * lives on the job and on the offer. */
function AtsOfferTemplatesSettings() {
  const { facilityId } = useFacility();
  const realFacility = facilityId === "all" ? null : facilityId;
  const canManage = useCanWithoutBuilding("hiring:manage");
  const canSeeFacilities = useCan("facility:read");

  const facilities = useQuery({
    ...orpc.administration.facilities.list.queryOptions({ input: { includeInactive: true } }),
    enabled: canSeeFacilities,
  });
  const facilityNames = new Map(
    (facilities.data ?? []).map((facility) => [facility.id, facility.name]),
  );

  return (
    <>
      <PageHeader
        title="Offer templates"
        meta="The letter a candidate is sent with a job offer, and the form they fill in to accept it."
      />

      {canManage && !realFacility ? (
        <OneBuildingCard
          prompt={
            "Every building's letters and forms are listed below. To add one, choose its building."
          }
        />
      ) : null}

      <Tabs defaultValue="letters" className="space-y-4">
        <TabsList>
          <TabsTrigger value="letters">Offer letters</TabsTrigger>
          <TabsTrigger value="acceptance">Acceptance forms</TabsTrigger>
        </TabsList>

        <TabsContent value="letters">
          <OfferLettersPanel
            facilityId={facilityId}
            realFacility={realFacility}
            canManage={canManage}
            facilityNames={facilityNames}
          />
        </TabsContent>

        <TabsContent value="acceptance">
          <AcceptanceFormsPanel
            facilityId={facilityId}
            realFacility={realFacility}
            canManage={canManage}
            facilityNames={facilityNames}
          />
        </TabsContent>
      </Tabs>
    </>
  );
}
